import React, { useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { useLocation } from 'react-router-dom';
import Header from 'Screens/Contextualization/Settings/components/Header';
import selectDashboardLayout from 'store/Preferences/dashboard/selectors';
import { saveDashboardLayout } from 'store/Preferences/dashboard/actions';
import { DASHBOARD_TYPES } from 'store/Preferences/dashboard/defaultLayouts';
import { setDashboardEditMode } from 'store/Dashboard/editModeActions';
import filterAndOrderWidgets from 'Screens/Contextualization/Dashboard/components/DashboardGrid/utils/filterAndOrderWidgets';
import reorderLayout from 'Screens/Contextualization/Dashboard/components/DashboardGrid/utils/reorderLayout';
import WIDGET_LABELS, {
  ADDABLE_WIDGETS
} from 'Screens/Contextualization/Dashboard/components/DashboardGrid/constants/widgetLabels';
import {
  EditorWrapper,
  EditorTitle,
  EditorSubtitle,
  PreviewArea,
  TileRow,
  Tile,
  TileDragHandle,
  TileName,
  TileGrowBadge,
  SettingsPanel,
  SettingsPanelHeader,
  SettingsWidgetName,
  SettingsRow,
  SettingsLabel,
  OptionButtons,
  OptionButton,
  RemoveTileButton,
  AddTileRow,
  AddTileButton,
  PickerList,
  PickerItem,
  EditFromDashboardRow,
  EditFromDashboardButton
} from './styled';
import { useIntl } from 'react-intl';

const TILE_COLORS = {
  remediation: '#f1ae62',
  bySeverity: '#9b59b6',
  workspaceRisk: '#dc4a4a',
  generalLatestFindings: '#4a90d9',
  activityFeedWidget: '#88cc3f',
  foundVsClosed: '#1abc9c',
  findingsByAsset: '#e67e22'
};

const GROW_OPTIONS = [1, 2, 3];
const VARIANT_OPTIONS = [
  { value: 'horizontal', label: 'Horizontal' },
  { value: 'vertical', label: 'Vertical' }
];
const WIDGETS_WITH_VARIANT = ['bySeverity'];

const DashboardLayoutEditor = ({ onClose }) => {
  const dispatch = useDispatch();
  const savedRows = useSelector((state) =>
    selectDashboardLayout(state, DASHBOARD_TYPES.GENERAL)
  );

  const [rows, setRows] = useState(() => filterAndOrderWidgets(savedRows));
  const [selected, setSelected] = useState(null);
  const [showPicker, setShowPicker] = useState(false);

  const location = useLocation();
  const isDashboard = /^\/feed\//.test(location.pathname);

  const intl = useIntl();
  const title = intl.formatMessage({ id: 'dashboard.widgets.settings.title' });
  const subtitle = intl.formatMessage({
    id: 'dashboard.widgets.settings.subtitle'
  });
  const addWidgetLabel = intl.formatMessage({
    id: 'dashboard.widgets.settings.addWidget'
  });
  const layoutLabel = intl.formatMessage({
    id: 'dashboard.widgets.settings.layout'
  });
  const removeLabel = intl.formatMessage({
    id: 'dashboard.widgets.settings.remove'
  });
  const editFromDashboardLabel = intl.formatMessage({
    id: 'dashboard.widgets.settings.editFromDashboard'
  });
  const widthLabel = intl.formatMessage({
    id: 'dashboard.widgets.settings.width'
  });

  const availableWidgets = useMemo(() => {
    const currentIds = new Set(rows.flat().map((w) => w.id));
    return ADDABLE_WIDGETS.filter((id) => !currentIds.has(id));
  }, [rows]);

  const handleDragEnd = useCallback(({ source, destination }) => {
    if (!destination) return;
    if (
      source.droppableId === destination.droppableId &&
      source.index === destination.index
    ) {
      return;
    }
    setRows((prev) => reorderLayout(prev, source, destination));
    setSelected(null);
  }, []);

  const handleTileClick = (rowIndex, colIndex, widget) => {
    if (selected?.rowIndex === rowIndex && selected?.colIndex === colIndex) {
      setSelected(null);
    } else {
      setSelected({ rowIndex, colIndex, ...widget });
    }
    setShowPicker(false);
  };

  const handleUpdate = useCallback(
    (changes) => {
      setRows((prev) =>
        prev.map((row, ri) =>
          row.map((widget, ci) =>
            ri === selected.rowIndex && ci === selected.colIndex
              ? { ...widget, ...changes }
              : widget
          )
        )
      );
      setSelected((prev) => (prev ? { ...prev, ...changes } : null));
    },
    [selected]
  );

  const handleRemove = useCallback(() => {
    setRows((prev) =>
      prev
        .map((row, ri) =>
          ri === selected.rowIndex
            ? row.filter((_, ci) => ci !== selected.colIndex)
            : row
        )
        .filter((row) => row.length > 0)
    );
    setSelected(null);
  }, [selected]);

  const handleAdd = useCallback((widgetId) => {
    setRows((prev) => [...prev, [{ id: widgetId, visible: true }]]);
    setShowPicker(false);
  }, []);

  const handleSave = () => {
    dispatch(
      saveDashboardLayout(DASHBOARD_TYPES.GENERAL, rows, { showLoading: true })
    );
    onClose();
  };

  const handleEditFromDashboard = () => {
    dispatch(saveDashboardLayout(DASHBOARD_TYPES.GENERAL, rows));
    dispatch(setDashboardEditMode(true));
    onClose();
  };

  return (
    <EditorWrapper>
      <Header onAccept={ handleSave } onClose={ onClose } />
      <div>
        <EditorTitle>{title}</EditorTitle>
        <EditorSubtitle>{subtitle}</EditorSubtitle>
      </div>
      <DragDropContext onDragEnd={ handleDragEnd }>
        <PreviewArea>
          {rows.map((row, rowIndex) => {
            const rowId = row.map((w) => w.id).join('|');
            return (
              <Droppable key={ rowId } droppableId={ rowId } direction="horizontal">
                {(provided, snapshot) => (
                  <TileRow
                    ref={ provided.innerRef }
                    { ...provided.droppableProps }
                    $isDraggingOver={ snapshot.isDraggingOver }
                  >
                    {row.map((widget, colIndex) => {
                      const isSelected =
                        selected?.rowIndex === rowIndex &&
                        selected?.colIndex === colIndex;

                      return (
                        <Draggable
                          key={ widget.id }
                          draggableId={ widget.id }
                          index={ colIndex }
                        >
                          {(dragProvided, dragSnapshot) => (
                            <Tile
                              ref={ dragProvided.innerRef }
                              { ...dragProvided.draggableProps }
                              $grow={ widget.grow }
                              $color={ TILE_COLORS[widget.id] }
                              $selected={ isSelected }
                              $isDragging={ dragSnapshot.isDragging }
                              onClick={ () =>
                                handleTileClick(rowIndex, colIndex, widget)
                              }
                            >
                              <TileDragHandle
                                { ...dragProvided.dragHandleProps }
                                onClick={ (e) => e.stopPropagation() }
                              >
                                ⠿
                              </TileDragHandle>
                              <TileName>
                                {WIDGET_LABELS[widget.id] || widget.id}
                              </TileName>
                              {widget.grow > 1 && (
                                <TileGrowBadge>{widget.grow}x</TileGrowBadge>
                              )}
                            </Tile>
                          )}
                        </Draggable>
                      );
                    })}
                    {provided.placeholder}
                  </TileRow>
                )}
              </Droppable>
            );
          })}
        </PreviewArea>
      </DragDropContext>
      <AddTileRow>
        <AddTileButton
          disabled={ availableWidgets.length === 0 }
          onClick={ () => {
            setShowPicker((v) => !v);
            setSelected(null);
          } }
        >
          + {addWidgetLabel}
        </AddTileButton>
      </AddTileRow>
      {showPicker && availableWidgets.length > 0 && (
        <PickerList>
          {availableWidgets.map((id) => (
            <PickerItem key={ id } onClick={ () => handleAdd(id) }>
              {WIDGET_LABELS[id] || id}
            </PickerItem>
          ))}
        </PickerList>
      )}
      {selected && (
        <SettingsPanel>
          <SettingsPanelHeader>
            <SettingsWidgetName>
              {WIDGET_LABELS[selected.id] || selected.id}
            </SettingsWidgetName>
            <RemoveTileButton onClick={ handleRemove }>
              {removeLabel}
            </RemoveTileButton>
          </SettingsPanelHeader>

          <SettingsRow>
            <SettingsLabel>{widthLabel}</SettingsLabel>
            <OptionButtons>
              {GROW_OPTIONS.map((value) => (
                <OptionButton
                  key={ value }
                  $active={ (selected.grow || 1) === value }
                  onClick={ () => handleUpdate({ grow: value }) }
                >
                  {value}x
                </OptionButton>
              ))}
            </OptionButtons>
          </SettingsRow>

          {WIDGETS_WITH_VARIANT.includes(selected.id) && (
            <SettingsRow>
              <SettingsLabel>{layoutLabel}</SettingsLabel>
              <OptionButtons>
                {VARIANT_OPTIONS.map(({ value, label }) => (
                  <OptionButton
                    key={ value }
                    $active={ (selected.variant || 'horizontal') === value }
                    onClick={ () => handleUpdate({ variant: value }) }
                  >
                    {label}
                  </OptionButton>
                ))}
              </OptionButtons>
            </SettingsRow>
          )}
        </SettingsPanel>
      )}
      {isDashboard &&
      <EditFromDashboardRow>
        <EditFromDashboardButton onClick={ handleEditFromDashboard }>
          {editFromDashboardLabel} →
        </EditFromDashboardButton>
      </EditFromDashboardRow>
      }
    </EditorWrapper>
  );
};

DashboardLayoutEditor.propTypes = {
  onClose: PropTypes.func.isRequired
};

export default DashboardLayoutEditor;
